import axios from "axios";
import { ValidationError } from "./error";
import i18n from "@/../i18n";
import toast from "@/plugins/toast";
import { authCookies } from "@/services/cookies";

export class Request {
  constructor({
    baseUrl = process.env.VUE_APP_BASE_URL,
    token = authCookies.getToken(),
    method,
    endpoint,
    data,
    params,
    responseType,
    notNeedToken,
    headers,
    silent,
    quiet,
  }) {
    this.progress = 0;
    this.baseUrl = baseUrl;
    this.method = method;
    this.endpoint = endpoint;
    this.data = data;
    this.token = token;
    this.params = params;
    this.responseType = responseType;

    this.notNeedToken = notNeedToken;
    this.headers = headers;
    this.silent = silent;
    this.quiet = quiet;
    // Create a cancel token source
    this.cancelTokenSource = axios.CancelToken.source();
  }

  async send(onDownloadProgress, onUploadProgress) {
    try {
      let response = await axios({
        baseURL: this.baseUrl,
        method: this.method,
        url: this.endpoint,
        data: this.data,
        headers: {
          ...(this.notNeedToken
            ? {}
            : { Authorization: `Bearer ${this.token}` }),
          Accept: "application/json",
          "x-locale": i18n.global.locale.value,
          ...this.headers,
        },
        params: this.params,
        responseType: this.responseType,
        onDownloadProgress: onDownloadProgress,
        onUploadProgress: onUploadProgress,
        cancelToken: this.cancelTokenSource.token,
      });
      if (
        !this.silent &&
        !this.quiet &&
        (response?.status == 200 || response?.status == 201) &&
        this.method != "get"
      ) {
        toast(i18n.global.t("operation_success"), { type: "success" });
      }

      return response.data;
    } catch (error) {
      if (this.quiet) {
        const quietError = new Error(this.quietMessage(error));
        quietError.status = error.response?.status;
        quietError.code = error.response?.data?.error || error.code;
        throw quietError;
      }
      if (error.code === "ERR_NETWORK") {
        const errorMessage = this.errorMapper(error.code);
        toast(errorMessage, { type: "error" });
        throw new ValidationError(errorMessage, "ERR_NETWORK");
      } else if (error.response?.data?.message || error.response?.data?.errors) {
        const payload = error.response.data;
        const message = payload.message;
        const errors = payload.errors;

        let firstMessage = "An unknown error occurred.";
        if (typeof message === "object" && !Array.isArray(message)) {
          const firstKey = Object.keys(message)[0];
          if (firstKey && Array.isArray(message[firstKey])) {
            firstMessage = message[firstKey][0];
          }
        } else if (typeof message === "string") {
          firstMessage = message;
        }

        if (
          firstMessage === "An unknown error occurred." &&
          errors &&
          typeof errors === "object"
        ) {
          const firstKey = Object.keys(errors)[0];
          if (firstKey && Array.isArray(errors[firstKey])) {
            firstMessage = errors[firstKey][0];
          }
        }

        toast(firstMessage, { type: "error" });
        const err = new ValidationError(
          firstMessage,
          payload.error || "USER_ERROR"
        );
        err.status = error.response.status;
        err.errors = errors || {};
        throw err;
      } else {
        const errorMessage = error.message || "An unknown error occurred.";
        toast(errorMessage, { type: "error" });
        const err = new Error(errorMessage);
        err.status = error.response?.status;
        throw err;
      }
    }
  }

  cancel() {
    this.cancelTokenSource.cancel("Request canceled by the user.");
  }

  quietMessage(error) {
    const payload = error.response?.data;
    const message = payload?.message;
    if (typeof message === "string" && message) return message;
    if (message && typeof message === "object" && !Array.isArray(message)) {
      const firstKey = Object.keys(message)[0];
      if (firstKey && Array.isArray(message[firstKey]) && message[firstKey][0]) {
        return message[firstKey][0];
      }
    }
    if (typeof payload?.error === "string" && payload.error) return payload.error;
    return error.message || "Request failed";
  }

  errorMapper(type) {
    const error = new Map([
      ["ERR_TIMED_OUT", "Internet_Error"],
      ["ERR_INTERNET_DISCONNECTED", "Internet_Error"],
      ["ERR_NETWORK", "Internet_Error"],
    ]);
    return error.get(type) || type;
  }
}
